import { SKILL_IDS, SimError, type Balance, type SkillId, type SkillMods, type SkillNode } from './types';

/** Skill levels as stored in the profile: absent = 0. */
export type SkillLevels = Partial<Record<SkillId, number>>;

export function defaultSkillMods(): SkillMods {
  return {
    turretDmgPct: 100,
    rangeAdd: 0,
    structureHpPct: 100,
    wallHpAdd: 0,
    soldierPct: 100,
    startGoldAdd: 0,
    waveGoldPct: 100,
    minePct: 100,
    priceDiscountPct: 0,
    heroPct: 100,
    heroSpeedPct: 100,
  };
}

/** Per-level effect of each node; the menu prints these, the sim applies them. Integers only. */
export const SKILL_STEP: Record<SkillId, number> = {
  turret_dmg: 4, // % damage of shooting buildings
  turret_range: 150, // milli-cells of range
  structure_hp: 6, // % building HP
  wall_hp: 10, // bunker HP
  soldier_power: 6, // % soldier HP and damage
  start_gold: 5, // gold at the start of a run
  wave_gold: 4, // % wave reward
  mine_income: 10, // % mine income
  cheap_shop: 3, // % off building prices
  hero_power: 3, // % hero HP and damage
  hero_speed: 5, // % hero speed
};

/** Fold bought levels into run modifiers. Unknown ids and levels below zero are ignored. */
export function skillMods(levels: SkillLevels | undefined): SkillMods {
  const m = defaultSkillMods();
  if (!levels) return m;
  const lv = (id: SkillId): number => Math.max(0, Math.floor(levels[id] ?? 0));
  m.turretDmgPct += SKILL_STEP.turret_dmg * lv('turret_dmg');
  m.rangeAdd += SKILL_STEP.turret_range * lv('turret_range');
  m.structureHpPct += SKILL_STEP.structure_hp * lv('structure_hp');
  m.wallHpAdd += SKILL_STEP.wall_hp * lv('wall_hp');
  m.soldierPct += SKILL_STEP.soldier_power * lv('soldier_power');
  m.startGoldAdd += SKILL_STEP.start_gold * lv('start_gold');
  m.waveGoldPct += SKILL_STEP.wave_gold * lv('wave_gold');
  m.minePct += SKILL_STEP.mine_income * lv('mine_income');
  m.priceDiscountPct += SKILL_STEP.cheap_shop * lv('cheap_shop');
  m.heroPct += SKILL_STEP.hero_power * lv('hero_power');
  m.heroSpeedPct += SKILL_STEP.hero_speed * lv('hero_speed');
  return m;
}

export function skillNode(id: SkillId, balance: Balance): SkillNode {
  const n = balance.skills.nodes.find((x) => x.id === id);
  if (!n) throw new SimError('unknown_skill', String(id));
  return n;
}

export function isSkillId(x: unknown): x is SkillId {
  return typeof x === 'string' && (SKILL_IDS as readonly string[]).includes(x);
}

/** Medals for raising a node from `level` to `level + 1`, or null at the cap. */
export function skillCost(id: SkillId, level: number, balance: Balance): number | null {
  const n = skillNode(id, balance);
  if (level >= n.maxLevel) return null;
  const t = balance.skills.costs;
  return t[Math.min(level, t.length - 1)] ?? null;
}

/** Why a node cannot be raised right now, or null when it can. */
export function skillBlocker(levels: SkillLevels, medals: number, id: SkillId, balance: Balance): 'max_level' | 'skill_locked' | 'no_medals' | null {
  const n = skillNode(id, balance);
  const cur = levels[id] ?? 0;
  if (cur >= n.maxLevel) return 'max_level';
  if (n.requires && (levels[n.requires] ?? 0) < 1) return 'skill_locked';
  const cost = skillCost(id, cur, balance);
  if (cost === null || medals < cost) return 'no_medals';
  return null;
}

/** Spend medals on one level of a node. Mutates `levels`; returns medals left and the new level. */
export function buySkill(levels: SkillLevels, medals: number, id: SkillId, balance: Balance): { medals: number; level: number } {
  if (!isSkillId(id)) throw new SimError('unknown_skill', String(id));
  const why = skillBlocker(levels, medals, id, balance);
  if (why) throw new SimError(why, id);
  const cur = levels[id] ?? 0;
  const cost = skillCost(id, cur, balance)!;
  levels[id] = cur + 1;
  return { medals: medals - cost, level: cur + 1 };
}

/** Total medals ever needed to max the whole tree; the menu shows progress against it. */
export function skillTreeTotal(balance: Balance): number {
  let total = 0;
  for (const n of balance.skills.nodes) for (let l = 0; l < n.maxLevel; l++) total += skillCost(n.id, l, balance) ?? 0;
  return total;
}
